// Venue-opaque trader profile URL slugs.
//
// A MEXC trader's id is literally "mexc:1234", so navigating to
// /traders/mexc:1234 would reveal the venue in the address bar. Normal users
// must not be able to tell which exchange a trader is on (2026-09-04, Nicho),
// so every profile link is encoded to an opaque, reversible slug.
//
// Encoded slugs carry a leading "~" marker (an unreserved URL char, never
// produced by base64url and never present in a raw trader_uid / name). The
// decoder treats anything without the marker as a legacy raw identifier, so
// old bookmarks and deep links (/traders/SonicLynx218, /traders/SIM_…) keep
// resolving unchanged.

import { isRealTraderName, looksLikeATraderId } from './traderSource';

const MARK = '~';

export function toTraderSlug(idOrName: string): string {
  if (!idOrName) return idOrName;
  try {
    const b64 = btoa(unescape(encodeURIComponent(idOrName)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
    return MARK + b64;
  } catch {
    return idOrName; // never break navigation over an encoding error
  }
}

export function fromTraderSlug(slug: string): string {
  if (!slug || slug[0] !== MARK) return slug; // legacy raw id/name
  try {
    const b64 = slug.slice(1).replace(/-/g, '+').replace(/_/g, '/');
    return decodeURIComponent(escape(atob(b64)));
  } catch {
    return slug;
  }
}

/****************
 * WHICH TRADER A PROFILE URL MEANS.
 *
 * Extracted from ProfileNew because that is where it broke and the shape of the
 * break made it untestable. The decode was applied in the useState initialiser
 * and NOT in the effect beside it, so the correct id was set and then
 * overwritten with the raw slug on the same mount -- every "View Trader" link
 * landed on a page that could not find the trader, for two days, and the only
 * automated check the frontend had was a typecheck, which cannot see it.
 *
 * One function, so there is one answer. A caller cannot decode in one place and
 * forget in another when there is only one place.
 *
 * `traders` is the loaded roster, or undefined while it is still loading -- in
 * which case the decoded id is returned as-is, because a URL means what it says
 * before anything has arrived to confirm it.
 ****************/
export function resolveTraderIdFromUrl(
  urlParam: string | null | undefined,
  traders: Array<{ id: string; trader_name?: string }> | null | undefined,
): string | null {
  if (!urlParam) return null;

  // ALWAYS decode first. Comparing a slug against a trader id matches nothing,
  // silently, and renders a profile page with no profile on it.
  const id = fromTraderSlug(urlParam);
  if (!traders || traders.length === 0) return id;

  const byId = traders.find((t) => t.id === id);
  if (byId) return byId.id;

  // Older links carry a pseudonym rather than an id. Matched loosely on
  // purpose: the names in those links were typed by people.
  const key = id.toLowerCase().replace(/\s+/g, '');
  const byName = traders.find(
    (t) => (t.trader_name || '').toLowerCase().replace(/\s+/g, '') === key,
  );
  if (byName) return byName.id;

  // Not on the roster. The decoded id is still the honest answer -- the trader
  // may be private, delisted, or simply not in this page's slice -- and
  // returning null here would turn "we cannot show you this one" into "you
  // asked for nothing".
  return id;
}

/****************
 * A TRADER'S ADDRESS IS HIS NAME. 2026-10-01.
 *
 * Guillaume: "Page address of a trader is supposed to be his name."
 *
 * It was, for the links that had a name to give: until 2026-09-04 the traders
 * list and the dashboard table went to /traders/SonicLynx218. The session page
 * and the trade window went to /traders/<raw id>, which for a MEXC trader is
 * /traders/mexc:1234 -- the exchange, in the address bar. Commit 44330a69
 * closed that by wrapping EVERY link in the "~" slug above, names included, so
 * the readable address went with it: /traders/~U29uaWNMeW54MjE4. And the slug
 * is base64, not a secret: anyone who decodes it reads the id.
 *
 * So, one builder and one resolver, and nothing else in the app writes a
 * trader into an address (traderAddress.test.ts reads the source for it):
 *
 *   traderPath(name, uid)        /traders/KeenHawk242
 *   copyTraderPath(name, uid)    /copy-trade?tab=live&trader=KeenHawk242
 *   resolveTraderAddress(...)    the address back to the trader
 *
 * The id still travels with an in-app click, in the router's `state`
 * (traderLinkState), which is not part of the address and cannot be copied
 * out of it. That is what makes a click exact even when a name is shared.
 *
 * ── Two traders, one name ───────────────────────────────────────────────────
 *
 * The name is derived from the id (store.Pseudonym: 40 x 40 x 900 names), so
 * two traders can share one. Measured on production 2026-10-01: 1,727 listed
 * traders, 1,726 distinct names -- ONE pair, both called SonicFlame265.
 *
 * A bare name that two listed traders share opens NEITHER of them: the page
 * shows the list filtered to that name and the user picks. For those traders
 * the address carries three more characters, /traders/SonicFlame265-k7q,
 * computed from the id (traderNameTag) and from nothing else -- not from the
 * order of a list, which changes, so the same address means the same trader
 * tomorrow. The tag says nothing about the exchange.
 *
 * ── Old addresses ───────────────────────────────────────────────────────────
 *
 * /traders/~slug, /traders/<raw id> and /profile/<either> are in bookmarks,
 * emails and Telegram messages already sent. Each still opens its trader, and
 * the page then REPLACES the address with the name (`canonical` below), for
 * an admin and a customer alike.
 ****************/

const compactName = (s: string) => (s || '').toLowerCase().replace(/\s+/g, '');
const idShaped = (s: string) => looksLikeATraderId(s) || /^SIM_/i.test(s);

/** Three characters that tell two same-named traders apart. From the id alone
 *  (FNV-1a), so it is the same on every device and on every day. */
export function traderNameTag(uid: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < uid.length; i++) {
    h ^= uid.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h % 46656).toString(36).padStart(3, '0');
}

/**
 * What stands for this trader in an address: the name. Not encoded -- callers
 * that build a query with URLSearchParams pass this as it is.
 *
 * With no real name to give (the server sent none), the opaque slug of the id:
 * the page it lands on replaces it with the name as soon as it knows it. Never
 * the id in the clear.
 */
export function traderAddress(name: string | null | undefined, uid: string | null | undefined): string {
  if (isRealTraderName(name)) return (name as string).trim();
  return uid ? toTraderSlug(uid) : '';
}

export const traderPath = (name: string | null | undefined, uid: string | null | undefined) =>
  `/traders/${encodeURIComponent(traderAddress(name, uid))}`;

export const copyTraderPath = (name: string | null | undefined, uid: string | null | undefined) =>
  `/copy-trade?tab=live&trader=${encodeURIComponent(traderAddress(name, uid))}`;

/** The id, for the router's `state`: it rides with the click and is not in the address. */
export const traderLinkState = (uid: string | null | undefined): { traderUid?: string } =>
  (uid ? { traderUid: uid } : {});

export interface TraderAddressResolution {
  /** The trader to show, or null when the address does not (yet) say. */
  id: string | null;
  /** The address this trader belongs at, unencoded, when the roster can say. */
  canonical: string | null;
  /** Ids that share the name, when the address cannot tell them apart. */
  ambiguous: string[];
  /** A name (with its tag, if it had one) that is not on the loaded roster: ask the server. */
  unknownName: string | null;
}

type Listed = { id: string; trader_name?: string };

/** The address a listed trader belongs at: the name, plus the tag only when
 *  another listed trader has the same name. */
export function canonicalTraderAddress(trader: Listed, traders: Listed[]): string {
  if (!isRealTraderName(trader.trader_name)) return toTraderSlug(trader.id);
  const name = (trader.trader_name as string).trim();
  const key = compactName(name);
  const shared = traders.some((t) => t.id !== trader.id && compactName(t.trader_name || '') === key);
  return shared ? `${name}-${traderNameTag(trader.id)}` : name;
}

const TAGGED = /^(.+)-([0-9a-z]{3})$/i;

/**
 * An address back to its trader.
 *
 * `urlParam` is the route segment or the ?trader= value, as the router hands
 * it over (already URL-decoded). `traders` is the loaded roster, or
 * null/undefined while it is loading. `hintUid` is the id that rode with an
 * in-app click (traderLinkState), if any.
 *
 * It never guesses: a name two traders share, with nothing to say which, comes
 * back as `ambiguous` with no id.
 */
export function resolveTraderAddress(
  urlParam: string | null | undefined,
  traders: Listed[] | null | undefined,
  hintUid?: string | null,
): TraderAddressResolution {
  const none: TraderAddressResolution = { id: null, canonical: null, ambiguous: [], unknownName: null };
  if (!urlParam) return none;
  const decoded = fromTraderSlug(urlParam).trim();
  if (!decoded) return none;

  // Before the roster: an id means what it says; a name waits, unless the
  // click that brought us here said who it meant.
  if (traders == null) {
    if (idShaped(decoded)) return { ...none, id: decoded };
    return { ...none, id: hintUid || null };
  }

  const at = (t: Listed): TraderAddressResolution => ({ ...none, id: t.id, canonical: canonicalTraderAddress(t, traders) });

  const byId = traders.find((t) => t.id === decoded);
  if (byId) return at(byId);
  // An id that is not on this roster: private, delisted, or outside this
  // page's slice. Still the trader that was asked for; the page learns the
  // name from the trader's own record and moves the address to it.
  if (idShaped(decoded)) return { ...none, id: decoded };

  const named = (name: string) => traders.filter((t) => compactName(t.trader_name || '') === compactName(name));

  const exact = named(decoded);
  if (exact.length === 1) return at(exact[0]);
  if (exact.length > 1) {
    const hinted = hintUid ? exact.find((t) => t.id === hintUid) : undefined;
    if (hinted) return at(hinted);
    return { ...none, ambiguous: exact.map((t) => t.id) };
  }

  const tagged = TAGGED.exec(decoded);
  if (tagged) {
    const tag = tagged[2].toLowerCase();
    const match = named(tagged[1]).filter((t) => traderNameTag(t.id) === tag);
    if (match.length === 1) return at(match[0]);
  }

  // A name nobody on this roster has. The click's own id if there was one;
  // otherwise only the server can say.
  if (hintUid) return { ...none, id: hintUid };
  return { ...none, unknownName: decoded };
}

/**
 * The server's answer for a name that was not on the roster
 * (GET /api/v1/traders/resolve). One trader, or nobody: two candidates and no
 * tag to choose between them is not an answer.
 */
export function pickFromNameLookup(
  unknownName: string | null | undefined,
  matches: Array<{ id: string }> | null | undefined,
): string | null {
  const found = matches || [];
  if (!unknownName || found.length === 0) return null;
  const tagged = TAGGED.exec(unknownName.trim());
  if (tagged) {
    const tag = tagged[2].toLowerCase();
    const byTag = found.filter((m) => traderNameTag(m.id) === tag);
    if (byTag.length === 1) return byTag[0].id;
  }
  return found.length === 1 ? found[0].id : null;
}

/**
 * The name address for a trader who is NOT on the loaded roster, given what
 * the server finds for his name -- or null when the name would not lead back
 * to him. An old address is only ever moved to one that can be opened again:
 * a name the server cannot resolve is a dead link the moment the page is
 * reloaded, and the id address it replaced was not.
 */
export function canonicalFromNameLookup(
  name: string | null | undefined,
  uid: string | null | undefined,
  matches: Array<{ id: string }> | null | undefined,
): string | null {
  const found = matches || [];
  if (!uid || !isRealTraderName(name) || !found.some((m) => m.id === uid)) return null;
  const n = (name as string).trim();
  if (found.length === 1) return n;
  const tag = traderNameTag(uid);
  // Two same-named traders whose tags also agree (1 in 46,656): no address tells them apart.
  if (found.filter((m) => traderNameTag(m.id) === tag).length !== 1) return null;
  return `${n}-${tag}`;
}

/** The name part of an address that may carry a tag: what to put in a search box. */
export function nameInAddress(urlParam: string | null | undefined): string {
  const decoded = fromTraderSlug(urlParam || '').trim();
  const tagged = TAGGED.exec(decoded);
  return tagged ? tagged[1] : decoded;
}
